// fi/en, the hub page's pattern carried over: English in the markup is
// the fallback, the Finnish dictionary overrides by key, the language
// is navigator.language unless the user picked one (localStorage
// "psypen_lang"), and the document's lang follows.
import { createSignal } from "solid-js";

export const LANGS = ["en", "fi"] as const;
export type Lang = (typeof LANGS)[number];

const KEY = "psypen_lang";

// The words of this slice; the screens that grow their own keep their
// own dictionaries beside their markup (the hub does the same).
const FI: Record<string, string> = {
  "tab.talk": "Puhu",
  // The hub page's own word for its live tab (page.go's dictionary).
  "tab.live": "Nyt",
  "tab.connections": "Yhteydet",
  "tab.machines": "Koneet",
  "tab.notes": "Muistiinpanot",
  "tab.files": "Tiedostot",
  "tab.settings": "Asetukset",
  // No in-app sign-in screen: the browser goes straight to the AMV login
  // (the hub's way). Only a failed start says so, as text.
  "signin.opening": "Avataan kirjautumista…",
  "signin.failed": "Kirjautuminen ei käynnistynyt — tarkista yhteys ja lataa uudelleen.",
  // The Talk screen: the microphone, the turn, the reply.
  "talk.intro": "Avaa mikrofoni ja puhu — tai pidä välilyönti pohjassa puhuaksesi.",
  "talk.mic.open": "Avaa mikrofoni",
  "talk.mic.opening": "Avataan mikrofonia…",
  "talk.mic.quiet": "Mikki auki — puhu",
  "talk.mic.speech": "Kuulee puhetta…",
  "talk.queued": "Vuoro jonossa — lähtee kun tämä vastaus valmistuu",
  "talk.answering": "Vastaa…",
  "talk.stop": "Keskeytä",
  "talk.mic.failed": "Mikrofonia ei saatu auki",
  "talk.nosocket": "Ei yhteyttä — vuoro ei lähtenyt.",
  "talk.dropped": "Yhteys katkesi — vuoro ei valmistunut.",
  "talk.first": "Ensimmäinen vastaus",
  "talk.afterspeech": "puheen lopusta",
  "talk.hold": "Pidä välilyönti pohjassa puhuaksesi",
  "talk.tap": "napsauta jättääksi mikrofonin auki",
  "lang.label": "Kieli",
  "live.nolines": "Ei vielä viivoja.",
  // The Live screen: the gate's box, the workers, the conversation.
  "live.gate.waiting": "Odottaa sinua",
  "live.gate.send": "Lähetä",
  "live.gate.cancel": "Peru",
  "live.gate.editable": "Muuta luonnosta sanomalla, mitä muutetaan.",
  "live.gate.until": "asti",
  "live.gate.failed": "Vastaus ei mennyt perille — yritä uudelleen.",
  "live.workers": "Työntekijät",
  "live.conv": "Keskustelu",
  "live.inserted": "Kirjoitettu kenttään",
  "live.waitingfor": "Odottaa sinua: ",
  "live.turnfailed": "Vuoro epäonnistui.",
  "live.failedword": "epäonnistui",
  "live.more": "lisää",
  "live.donecount": "valmista",
  "live.unread": "lukematon",
  "live.now.idle": "Mitään ei juuri nyt tapahdu.",
  // The contract's state words (Line.description), as the worker's pill
  // shows them — and the worker's own two, for when the description is
  // empty.
  "live.state.working": "töissä",
  "live.state.needs you": "odottaa sinua",
  "live.state.sleeping": "nukkuu",
  "live.state.failed": "epäonnistui",
  "live.state.idle": "vapaa",
  "live.state.done": "valmis",
  "live.state.queued": "jonossa",
  "live.state.holding": "pidossa",
  // An inbox item at the gate (kind question/permission): the box shows
  // it, the answer is not the send/cancel pair.
  "live.gate.question": "Kysymys",
  "live.gate.permission": "Lupa",
  "live.gate.elsewhere": "vastataan istuntojen näytöltä",
  "live.upload.add": "Lisää tiedostoja",
  "live.upload.camera": "Kuva",
  "live.upload.sending": "Lähetetään…",
  "live.upload.one": "1 tiedosto työtilassa: {names}",
  "live.upload.many": "{n} tiedostoa työtilassa: {names}",
  "live.upload.busy": "Odota, edellinen lähetys on vielä kesken.",
  "live.upload.toobig": "Liian suuri tai liian monta tiedostoa kerralla.",
  "live.upload.waiting": "Työtilassa on jo liikaa odottamassa — palkkaa työntekijä tai poista tiedostoja.",
  "live.upload.failed": "Tiedoston lähetys ei onnistunut — yritä uudelleen.",
  "status.live": "yhteys auki",
  "status.reconnecting": "yhdistää uudelleen",
  "status.signedout": "kirjaudu uudelleen",
  "status.cached": "ei yhteyttä — viimeisin tila",
  "status.connecting": "yhdistää",
  "status.closed": "suljettu",
  "screen.placeholder": "Tämä näyttö rakennetaan omassa viipaleessaan.",
};

// The pick, apart so it is testable: a stored choice wins, else the
// browser's language decides between the two.
export function detectLang(stored: string | null, navLang: string): Lang {
  if (stored && (LANGS as readonly string[]).includes(stored)) return stored as Lang;
  return (navLang || "en").toLowerCase().startsWith("fi") ? "fi" : "en";
}

function readLang(): Lang {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(KEY);
  } catch {
    // Storage the page cannot read reads as "no pick".
  }
  return detectLang(stored, navigator.language || "en");
}

const initial = readLang();
const [lang, setLangSignal] = createSignal<Lang>(initial);

// The document says what it is reading from the first paint, not only
// after a pick (the hub's paint does the same at load). The plain
// initial, not the signal: this is one synchronisation, not tracking.
if (typeof document !== "undefined") document.documentElement.lang = initial;

export { lang };

export function setLang(next: Lang): void {
  setLangSignal(next);
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // A pick the page cannot keep still holds for this run.
  }
  document.documentElement.lang = next;
}

// The upload's words, English here beside the Finnish above.
const EN: Record<string, string> = {
  "live.upload.add": "Add files",
  "live.upload.camera": "Photo",
  "live.upload.sending": "Uploading…",
  "live.upload.one": "1 file in the workspace: {names}",
  "live.upload.many": "{n} files in the workspace: {names}",
  "live.upload.busy": "Wait, the last upload is still going.",
  "live.upload.toobig": "Too big, or too many files at once.",
  "live.upload.waiting": "The workspace has too much waiting — hire a worker or remove uploads.",
  "live.upload.failed": "The upload did not go through — try again.",
};

// The hub's t, verbatim in shape: the Finnish override when the window
// is Finnish, else the English this file or the caller carries, else the key.
export function t(key: string, english: string): string {
  return (lang() === "fi" && FI[key]) || EN[key] || english || key;
}
